'use client';
import { Box, SxProps, Theme, useTheme } from '@mui/material';

import { FC, useMemo, useState } from 'react';
import { useAppSelector, useTranslation, useWindowDimensions } from '@/hook';

import { numberWithCommas } from '@/utils/helper.util';
import { ErrorOutline } from '@mui/icons-material';
import { useAnalytics } from '@/integrations';

import CarSoldOverlay from '@/components/car-details//CarProfileNew/SimilarCars/CarSoldOverlay';
import { PMT } from '@/utils';
import {
    DisplayedCarInfoInterface,
    InterestRate,
    LenderWithInterestRates,
} from '@/types';
import { ComparisonIcon } from '../../../../public/Images';

import Button from '../Button/button.component';
import FinancierButton from '@/components/financing/FinancierButton';
import FinancierComparison from '@/components/financing/FinancierComparisonDesktop';
import SliderUnControlled from '@/components/shared/SliderUncontrolled';
import SylndrSwitch from '@/components/shared/Switch/index';
import Typography from '@/components/shared/typography/typography.component';
import FinancierComparisonMobile from '@/components/financing/FinancierComparisonMobile';

import Image from 'next/image';

const defaultDownpayment = 50;
const defaultMonths = 60;

const FinanceCalculator: FC<{
    carPrice: number;
    canApply?: boolean;
    lenders?: LenderWithInterestRates[];
    eventName?: string;
    carName?: string;
    isSold?: boolean;
    isPurchaseInProgress?: boolean;
    carInfo: DisplayedCarInfoInterface;
    mobileButtons: JSX.Element;
}> = ({
    carPrice,
    canApply,
    lenders,
    eventName,
    carName,
    isSold,
    carInfo,
    mobileButtons,
    isPurchaseInProgress,
}) => {
    const [downpayment, setDownpayment] = useState(defaultDownpayment);
    const [months, setMonths] = useState(defaultMonths);
    const [userInteracted, setUserInteracted] = useState(false);
    const [carSellBan, setCarSellBan] = useState<boolean>(true);
    const [financiersOpen, setFinanciersOpen] = useState<boolean>(false);

    const { t } = useTranslation();
    const { selectedCar } = useAppSelector(state => state.checkout);
    const { trackEvent } = useAnalytics();
    const theme = useTheme();
    const { IS_DESKTOP } = useWindowDimensions();

    const downpaymentAmount = useMemo(
        () => carPrice * (downpayment / 100),
        [carPrice, downpayment]
    );

    const loanAmount = useMemo(
        () => carPrice - downpaymentAmount,
        [carPrice, downpaymentAmount]
    );

    const interestRates = useMemo<InterestRate[]>(() => {
        return (
            lenders
                ?.filter(lender => lender.maxTenor >= months)
                ?.reduce((acc: InterestRate[], lender) => {
                    const interestRates = carSellBan
                        ? lender?.interestRateCarSellBan
                        : lender?.interestRate;
                    return acc.concat(interestRates);
                }, []) || []
        );
    }, [lenders, carSellBan, months, downpayment]);

    const minDownPayment = useMemo(() => {
        const downPayment = [...(interestRates || [])]
            ?.sort((a, b) => b.downpayment - a.downpayment)
            .pop()?.downpayment;
        return downPayment ?? 0;
    }, [interestRates]);

    const maxDownPayment = useMemo(() => {
        const downPayment = [...(interestRates || [])]
            ?.sort((a, b) => a.downpayment - b.downpayment)
            .pop()?.downpayment;
        return downPayment ?? 95;
    }, [interestRates]);

    if (!interestRates) {
        return null;
    }

    // eslint-disable-next-line react-hooks/rules-of-hooks
    const paymentPerMonth = useMemo(() => {
        if (!interestRates) {
            return null;
        }
        const available = interestRates.filter(
            a => a.downpayment === downpayment && a.active
        );
        if (!available.length) return t('Not Available');

        const carPaymentPerMonthMin = numberWithCommas(
            PMT(
                interestRates
                    .filter(a => a.downpayment === downpayment && a.active)
                    ?.sort((a, b) => a.interestRate - b.interestRate)?.[0]
                    ?.interestRate / 100 || 0,
                months,
                loanAmount
            )
        );
        const carPaymentPerMonthMax = numberWithCommas(
            PMT(
                interestRates
                    .filter(a => a.downpayment === downpayment && a.active)
                    ?.sort((a, b) => b.interestRate - a.interestRate)?.[0]
                    ?.interestRate / 100 || 0,
                months,
                loanAmount
            )
        );

        return `${t(
            'EGP'
        )}  ${carPaymentPerMonthMin} - ${carPaymentPerMonthMax}`;
    }, [interestRates, months, loanAmount]);

    const fireFinancingEvent = () => {
        if (!userInteracted) {
            trackEvent(eventName || 'Action: Thank You Financing', {
                interaction: true,
                carName: carName || selectedCar?.sfVehicleName,
            });

            setUserInteracted(true);
        }
    };

    return (
        <Box>
            {IS_DESKTOP ? (
                <FinancierComparison
                    isOpen={financiersOpen}
                    setOpen={setFinanciersOpen}
                    lenders={lenders}
                    carSellBan={carSellBan}
                    setCarSellBan={setCarSellBan}
                    monthlyInstallments={paymentPerMonth}
                    carInfo={carInfo}
                />
            ) : (
                <FinancierComparisonMobile
                    isOpen={financiersOpen}
                    setOpen={setFinanciersOpen}
                    lenders={lenders}
                    carSellBan={carSellBan}
                    setCarSellBan={setCarSellBan}
                    monthlyInstallments={paymentPerMonth}
                    carInfo={carInfo}
                    downpayment={downpayment}
                    months={months}
                    mobileButtons={mobileButtons}
                />
            )}
            <Box
                sx={{
                    border: 1,
                    borderColor: '#eaecf0',
                    borderRadius: '8px',
                    padding: '16px',
                    mb: 1,
                    position: 'relative',
                }}
            >
                {isSold || isPurchaseInProgress ? (
                    <>
                        <Box sx={{ my: '80px' }} />
                        <Button />
                        <Box sx={{ my: '40px' }} />
                        <CarSoldOverlay
                            style={{ paddingTop: '0px' }}
                            type={
                                isPurchaseInProgress
                                    ? 'PURCHASE_IN_PROGRESS'
                                    : 'SOLD'
                            }
                        />
                    </>
                ) : (
                    <>
                        <Typography
                            variant="body2Medium"
                            style={{ color: '#002D5D' }}
                        >
                            {t('Downpayment')}
                        </Typography>

                        <Box sx={{ my: 4 }} />

                        <SliderUnControlled
                            value={downpayment}
                            aria-label="Downpayment"
                            defaultValue={defaultDownpayment}
                            valueLabelDisplay="on"
                            step={5}
                            marks
                            max={maxDownPayment}
                            min={minDownPayment}
                            onChange={(_, value) => {
                                fireFinancingEvent();
                                setDownpayment(value as number);
                            }}
                            valueLabelFormat={value => (
                                <Typography variant={'captionMedium'}>
                                    {value + '%'}
                                </Typography>
                            )}
                            sx={styles.slider}
                        />

                        <Typography
                            variant="body2Medium"
                            style={{ color: '#002D5D' }}
                        >
                            {t('Loan Tenor')}
                        </Typography>

                        <Box sx={{ my: 4 }} />

                        <SliderUnControlled
                            value={months}
                            aria-label="Months"
                            defaultValue={defaultMonths}
                            valueLabelDisplay="on"
                            step={12}
                            marks
                            min={12}
                            max={84}
                            onChange={(_, value) => {
                                fireFinancingEvent();
                                setMonths(value as number);
                            }}
                            valueLabelFormat={value => (
                                <Typography variant={'captionMedium'}>
                                    {value + ' ' + t('Months')}
                                </Typography>
                            )}
                            sx={styles.slider}
                        />

                        <Box sx={{ mb: 3 }}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    padding: '16px',
                                    backgroundColor: '#FCFCFD',
                                    border: '1px solid #D0D5DD',
                                    borderRadius: '8px',
                                    gap: '12px',
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography
                                        variant="body2SemiBold"
                                        style={{ color: '#344054' }}
                                    >
                                        {t('Car Price')}
                                    </Typography>
                                    <Typography
                                        variant="body2Medium"
                                        style={{ color: '#667085' }}
                                    >
                                        {t('EGP') +
                                            ' ' +
                                            numberWithCommas(
                                                Math.ceil(carPrice)
                                            )}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography
                                        variant="body2SemiBold"
                                        style={{ color: '#344054' }}
                                    >
                                        {t('Downpayment')}
                                    </Typography>
                                    <Typography
                                        variant="body2Medium"
                                        style={{ color: '#667085' }}
                                    >
                                        {t('EGP') +
                                            ' ' +
                                            numberWithCommas(
                                                Math.ceil(
                                                    carPrice *
                                                        (downpayment / 100)
                                                )
                                            )}
                                    </Typography>
                                </Box>

                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <Typography
                                        variant="body2SemiBold"
                                        style={{ color: '#344054' }}
                                    >
                                        {t('Loan Amount')}
                                    </Typography>
                                    <Typography
                                        variant="body2Medium"
                                        style={{ color: '#667085' }}
                                    >
                                        {t('EGP') +
                                            ' ' +
                                            numberWithCommas(
                                                Math.ceil(loanAmount)
                                            )}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                gap: '12px',
                            }}
                        >
                            <Typography
                                variant="body1"
                                style={{ color: '#002D5D' }}
                            >
                                {t('financierOptions')}
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    variant="body2Medium"
                                    style={{ color: '#344054' }}
                                >
                                    {t('monthly')}
                                </Typography>
                                <Typography
                                    variant="body2SemiBold"
                                    style={{ color: '#009B5D' }}
                                >
                                    {paymentPerMonth}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                }}
                            >
                                <Typography
                                    variant="body2Medium"
                                    style={{ color: '#344054' }}
                                >
                                    {t('carSellBan')}
                                </Typography>
                                <SylndrSwitch
                                    onChange={e =>
                                        setCarSellBan(e.target.checked)
                                    }
                                    checked={carSellBan}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ my: 4 }} />

                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                            {lenders?.map(lender => (
                                <FinancierButton
                                    key={lender.id}
                                    lender={lender}
                                    carSellBan={carSellBan}
                                    downpayment={downpayment}
                                    months={months}
                                />
                            ))}
                        </Box>
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'row',
                                justifyContent: 'flex-start',
                                gap: '8px',
                                marginBottom: '12px',
                                cursor: 'pointer',
                                alignItems: 'center',
                            }}
                            onClick={() => setFinanciersOpen(true)}
                        >
                            <Image src={ComparisonIcon} alt="ComparisonIcon" />
                            <Typography variant="body2Medium">
                                {t('viewComparison')}
                            </Typography>
                        </div>
                        {!canApply && (
                            <>
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        justifyContent: 'flex-start',
                                        padding: '16px',
                                        backgroundColor: '#FCFCFD',
                                        border: '1px solid #D0D5DD',
                                        borderRadius: '8px',
                                        gap: '12px',
                                    }}
                                >
                                    <ErrorOutline
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            mx: 1,
                                        }}
                                    />
                                    <Typography
                                        variant="body2Medium"
                                        style={{
                                            color: theme.palette.text.secondary,
                                        }}
                                    >
                                        {t('applyForFinanceHint')}
                                    </Typography>
                                </Box>
                            </>
                        )}
                        <Box sx={{ my: 2 }} />
                    </>
                )}
            </Box>
        </Box>
    );
};

const styles: Record<string, SxProps<Theme>> = {
    slider: {
        height: '8px',
        '& .MuiSlider-thumb': {
            bgcolor: '#fff',
            borderWidth: '20px',
            border: 1.5,
        },
        '& .MuiSlider-rail': {
            bgcolor: '#EAECF0',
            opacity: 1,
        },
        '& .MuiSlider-mark': {
            height: '6px',
            bgcolor: '#D0D5DD',
        },
        '& .MuiSlider-markActive': {
            height: '0px',
        },
        '& .MuiSlider-valueLabel': {
            bgcolor: '#FFFFFF',
            boxShadow: 6,
            padding: '8px 12px',
            borderRadius: '8px',
            ':before': {
                display: 'none',
            },
        },
        '& .MuiSlider-valueLabelLabel': {
            color: '#000',
        },
    },
};
export default FinanceCalculator;
